import type { FurniturePart } from "../../types";
import { clamp, divide, part, rect, resetPartIds, seam } from "../geometry";
import { bool, num, type BuildContext, type BuildResult, type FurnitureTemplate } from "./types";

/**
 * Bed: headboard along the back (-y) edge, pillows just in front of it, a
 * mattress running to the foot (+y), and a blanket covering the lower
 * two-thirds with a turned-down fold line. `frontKind: "sleep"` — the foot
 * is the +y edge.
 */
export const bedTemplate: FurnitureTemplate = {
  id: "bed",
  label: "Bed",
  frontKind: "sleep",
  defaultMaterial: "fabric",
  params: [
    { id: "pillows", label: "Pillows", type: "int", min: 0, max: 4, default: 2 },
    { id: "headboard", label: "Headboard", type: "bool", default: true },
    { id: "blanket", label: "Blanket", type: "bool", default: true },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const pillows = num(params, "pillows", 2);
    const headboard = bool(params, "headboard", true);
    const blanket = bool(params, "blanket", true);
    const HB = headboard ? clamp(d * 0.045, 2.5, 4) : 0;
    const frameH = Math.min(h, 14);
    const parts: FurniturePart[] = [];

    // Frame.
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, frameH, { cornerRadius: 1 }));
    if (headboard) {
      parts.push(part("headboard", rect(-w / 2, -d / 2, w, HB), 0, Math.max(h, frameH + 20), { cornerRadius: 0.75 }));
    }

    // Mattress.
    const mx = -w / 2 + 1;
    const my = -d / 2 + HB + 0.5;
    const mw = w - 2;
    const md = d - HB - 1.5;
    parts.push(part("mattress", rect(mx, my, mw, md), frameH, h, { cornerRadius: 2 }));

    // Pillows.
    const ph = clamp(d * 0.17, 10, 16);
    if (pillows > 0) {
      const cells = divide(mx + 2, mw - 4, pillows, 2);
      for (const c of cells) {
        parts.push(
          part("pillow", rect(c.start, my + 2, c.size, ph), h, h + 3, {
            cornerRadius: clamp(Math.min(c.size, ph) * 0.2, 2, 4),
          })
        );
      }
    }

    // Blanket from below the pillows to the foot, with a turned-down fold.
    if (blanket) {
      const by = my + 2 + ph + 6;
      const bd = my + md - by;
      if (bd > 6) {
        parts.push(
          part("blanket", rect(mx + 0.5, by, mw - 1, bd), h, h + 1.5, {
            cornerRadius: 1.5,
            seams: [seam(mx + 2, by + 4, mx + mw - 2, by + 4)],
          })
        );
      }
    }
    return { parts };
  },
};

export const nightstandTemplate: FurnitureTemplate = {
  id: "nightstand",
  label: "Nightstand",
  frontKind: "none",
  defaultMaterial: "wood-mid",
  params: [],
  build({ w, d, h }: BuildContext): BuildResult {
    resetPartIds();
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: 0.75 }));
    parts.push(
      part("top", rect(-w / 2 + 0.75, -d / 2 + 0.75, w - 1.5, d - 1.5), h, h + 0.5, {
        cornerRadius: 0.5,
      })
    );
    return { parts };
  },
};
